import { useEffect, useState, RefObject } from 'react';

/**
 * Generic hook to detect when an element becomes sticky.
 * 
 * Uses Intersection Observer to track the sentinel element just above
 * the sticky element. When the sentinel is no longer visible, the element is sticky.
 * 
 * **Generic**: Works with any sticky element. No domain knowledge.
 * 
 * @param sentinelRef - Reference to sentinel element (positioned just above sticky element)
 * @returns True if the sticky element is currently stuck
 * 
 * @example
 * ```tsx
 * function MyStickyComponent() {
 *   const sentinelRef = useRef<HTMLDivElement>(null);
 *   const isSticky = useStickyDetection(sentinelRef);
 *   
 *   return (
 *     <>
 *       <div ref={sentinelRef} style={{ height: 1 }} />
 *       <div style={{ position: 'sticky', top: 0 }}>
 *         {isSticky ? 'Stuck!' : 'Not stuck'}
 *       </div>
 *     </>
 *   );
 * }
 * ```
 */
export function useStickyDetection(sentinelRef: RefObject<HTMLElement>): boolean {
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    // When the sentinel is NOT visible, the sticky element is stuck
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px',
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [sentinelRef]);

  return isSticky;
}
